import { useMemo } from 'react';
import { capitalizeFirstLetter, FieldTypes } from '@common-fe/common-fe';

import { EMPTY_FIELD_VALUE } from '@/common/constants';
import regexp from '@/common/regexp';
import { lockboxReasonTitle, LockboxStatus, LockboxType, lockboxTypeTitle, PROCESSED_MANUALLY_STATUS_TITLE, RETURN_MAIL_PROCESSED_STATUS_TITLE } from '@/modules/employer/components/Lockbox/lockbox.types';
import useLockboxDetailsStore from '@/modules/employer/components/Lockbox/LockboxDetails/stores/useLockboxDetails.store';

const useGenericFormStatusFields = () => {
  const { originalLockbox } = useLockboxDetailsStore();
  const lockboxStatus = originalLockbox?.lockboxStatus;
  const lockboxType = originalLockbox?.lockboxType;
  const isReturnedMailProcessed = lockboxType === LockboxType.RETURNED_MAIL
    && lockboxStatus === LockboxStatus.APPROVED;
  const statusTitle = isReturnedMailProcessed
    ? RETURN_MAIL_PROCESSED_STATUS_TITLE
    : lockboxStatus === LockboxStatus.PROCESSED_MANUALLY
      ? PROCESSED_MANUALLY_STATUS_TITLE
      : capitalizeFirstLetter(lockboxStatus?.replace(regexp.DASH_SYMBOL, ' ')?.toLowerCase() || '');
  const formName = originalLockbox?.formName || (originalLockbox?.lockboxType
    ? lockboxTypeTitle[originalLockbox.lockboxType]
    : '');

  const reasonTitle = useMemo(() => originalLockbox?.reason
    ? lockboxReasonTitle?.[originalLockbox?.reason]
    : '', [originalLockbox?.reason]);

  const fields = useMemo(() => {
    if (lockboxStatus === LockboxStatus.PROCESSED_MANUALLY) {
      return [
        {
          type: FieldTypes.Text as const,
          name: 'formName',
          label: 'Form name',
          value: formName || EMPTY_FIELD_VALUE,
        },
        {
          type: FieldTypes.Text as const,
          name: 'approvalStatus',
          label: 'Approval status',
          value: statusTitle || EMPTY_FIELD_VALUE,
        },
        ...(originalLockbox?.notes ? [{
          type: FieldTypes.Text as const,
          name: 'note',
          label: 'Note',
          value: originalLockbox.notes,
        }] : []),
      ];
    }

    if (lockboxStatus === LockboxStatus.PENDING) {
      return [
        {
          type: FieldTypes.Text as const,
          name: 'reason',
          label: 'Reason',
          value: reasonTitle || EMPTY_FIELD_VALUE,
        },
        {
          type: FieldTypes.Text as const,
          name: 'approvalStatus',
          label: 'Approval status',
          value: statusTitle || EMPTY_FIELD_VALUE,
        },
        {
          type: FieldTypes.Text as const,
          name: 'note',
          label: 'Note',
          value: originalLockbox?.notes || EMPTY_FIELD_VALUE,
        },
      ];
    }

    if (lockboxStatus === LockboxStatus.CANCELED) {
      return [
        {
          type: FieldTypes.Text as const,
          name: 'approvalStatus',
          label: 'Approval status',
          value: statusTitle || EMPTY_FIELD_VALUE,
        },
        {
          type: FieldTypes.Text as const,
          name: 'note',
          label: 'Note',
          value: originalLockbox?.notes || EMPTY_FIELD_VALUE,
        },
      ];
    }

    return [];
  }, [lockboxStatus, originalLockbox, statusTitle, formName, reasonTitle]);

  return fields;
};

export default useGenericFormStatusFields;
